import React, { useContext, useState } from 'react';
import { InfluencerDataContext } from '../../../contexts/InfluencerDataContext';
import { Box, Text, VStack } from '@chakra-ui/react';
import WalletBalanceHero from './WalletBalanceHero';
import ProfileWhopWalletWithdrawFlow from './ProfileWhopWalletWithdrawFlow';
import { useWalletPendingBreakdown } from '../hooks/useWalletPendingBreakdown';
import { useWalletBootstrap } from '../hooks/useWalletBootstrap';
import { useSetupPayouts } from '../hooks/useSetupPayouts';
import {
  resolveEffectiveWhopCompanyId,
  type SetupWhopCompanyBinding,
} from '../utils/walletSetupCompanyUtils';
import { ff } from '../../../theme';

export interface ProfileWhopWalletCardProps {
  influencerId: string | undefined;
  whopCompanyId: string | undefined;
  onOpenWallet?: () => void;
}

const CONNECT_WHOP_ERROR_PREFIX = 'Please connect your Whop account';

/**
 * Profile wallet preview — same Figma hero as /wallet, compact placement under stats.
 */
const ProfileWhopWalletCard: React.FC<ProfileWhopWalletCardProps> = ({
  influencerId,
  whopCompanyId,
  onOpenWallet,
}) => {
  const { isSwitchedAccount } = useContext(InfluencerDataContext);
  const blockCreatorPayoutActions = isSwitchedAccount;
  const [setupBinding, setSetupBinding] = useState<SetupWhopCompanyBinding | undefined>(undefined);
  const effectiveWhopCompanyId = resolveEffectiveWhopCompanyId(
    whopCompanyId,
    influencerId,
    setupBinding,
  );

  const {
    session,
    kycApproved,
    transferredAmount,
    failedTransferCount,
    pendingBreakdown,
    pendingItems,
    pendingDataLoaded,
    loading,
    balanceRefreshing,
    error: loadError,
    refreshAvailableBalance,
    setKycApproved,
  } = useWalletBootstrap({ influencerId, whopCompanyId: effectiveWhopCompanyId });
  const { breakdown: resolvedPendingBreakdown } = useWalletPendingBreakdown({
    bootstrapBreakdown: pendingBreakdown,
    bootstrapItems: pendingItems,
    bootstrapLoaded: pendingDataLoaded,
  });
  const { setupPayouts, connecting, connectError } = useSetupPayouts({
    influencerId,
    setKycApproved,
    onCompanyId: (companyId) => {
      if (influencerId) {
        setSetupBinding({ influencerId, companyId });
      }
    },
    onWithdrawalComplete: refreshAvailableBalance,
  });

  const canEmbedWhop =
    Boolean(effectiveWhopCompanyId) &&
    typeof window !== 'undefined' &&
    window.location.protocol === 'https:' &&
    session &&
    !session.requiresHttps &&
    Boolean(session.token && session.companyId);

  const displayError = connectError ?? loadError;
  const needsWhopConnection =
    !effectiveWhopCompanyId || (loadError?.includes(CONNECT_WHOP_ERROR_PREFIX) ?? false);
  const hasWhopAccount = Boolean(effectiveWhopCompanyId) && !needsWhopConnection;

  if (loading && effectiveWhopCompanyId && !needsWhopConnection) {
    return (
      <Box>
        <WalletBalanceHero balanceLoading loading />
      </Box>
    );
  }

  const heroProps = {
    availableBalance: session?.availableBalance,
    balanceLoading: loading || balanceRefreshing,
    pendingBreakdown: resolvedPendingBreakdown,
    hasWhopAccount,
    withdrawEnabled: canEmbedWhop && !blockCreatorPayoutActions,
    connecting,
    onConnect: blockCreatorPayoutActions
      ? undefined
      : () => {
          void setupPayouts();
        },
    onOpenWallet,
  };

  const statusMessages = (
    <>
      {transferredAmount != null && transferredAmount > 0 && (
        <Text fontSize="13px" lineHeight="1.4" color={ff.body} px="4px">
          ${transferredAmount.toFixed(2)} was added to your Whop balance.
        </Text>
      )}
      {failedTransferCount > 0 && (
        <Text fontSize="13px" lineHeight="1.4" color={ff.danger} px="4px">
          {failedTransferCount} payout{failedTransferCount === 1 ? '' : 's'} could not be transferred. Open Wallet to retry.
        </Text>
      )}
      {effectiveWhopCompanyId && session?.requiresHttps && (
        <Text fontSize="13px" lineHeight="1.4" color={ff.text2} px="4px">
          Open this app from your HTTPS URL to see your Whop balance here.
        </Text>
      )}
      {displayError && (
        <Text fontSize="13px" lineHeight="1.4" color={ff.danger} px="4px">
          {displayError}
        </Text>
      )}
    </>
  );

  return (
    <Box>
      <VStack align="stretch" spacing="8px">
        {canEmbedWhop && session ? (
          <ProfileWhopWalletWithdrawFlow
            heroProps={heroProps}
            companyId={session.companyId}
            accessToken={session.token}
            kycApproved={kycApproved}
            onKycApprovalChange={setKycApproved}
            influencerId={influencerId}
            onRefreshBalance={refreshAvailableBalance}
          />
        ) : (
          <WalletBalanceHero {...heroProps} />
        )}
        {statusMessages}
      </VStack>
    </Box>
  );
};

export default ProfileWhopWalletCard;
